import { useState } from "react";
import { Link, useParams, useSearchParams } from 'react-router-dom';

import DecisionModal from '@/components/custom/InfoPopups/DecisionModal';
import ErrorPopup from '@/components/custom/InfoPopups/ErrorPopup';
import SuccessPopup from "@/components/custom/InfoPopups/SuccessPopup";

import LoaderWithBackdrop from '@/components/custom/LoaderWithBackdrop';
import { useProjectStore } from "@/lib/zustand/useStore";

import { ListRow } from "@/components/molecules/ListRow";
import { SectionTitle } from "@/components/molecules/SectionTitle";
import { Archive, ArchiveRestore, List, ListTree, Loader, Pencil, Trash2, } from "lucide-react";

import { ScrollArea } from "@/components/ui/scroll-area";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useSurveyStore } from "@/lib/zustand/useStore";


import { AddQuestionModal } from '@/features/question/components/organisms/AddQuestionModal';
import { EditQuestionModal } from "@/features/question/components/organisms/EditQuestionModal/index"; // TODO: Refactor this component

import { TooltipButton } from "@/components/atoms/TooltipButton";
import { questionsSortConfig } from "@/hooks/useSortBy/sortConfigs";
import { useSortByWithConfig } from "@/hooks/useSortBy/useSortByWithConfig";
import { cn } from "@/lib/utils";
import { AddQuestionIconButton } from "../../../../../components/atoms/AddQuestionIconButton";
import { SortArchiveButton } from "../../../../../components/atoms/SortArchiveButton";
import { QuestionFormSchemaType } from "../QuestionForm";
import { useAddQuestion } from "@/features/question/hooks/useMutation/useAddQuestion";
import { useArchiveQuestion } from "@/features/question/hooks/useMutation/useArchiveQuestion";
import { useRestoreQuestion } from "@/features/question/hooks/useMutation/useRestoreQuestion";
import { useDeleteQuestion } from "@/features/question/hooks/useMutation/useDeleteQuestion";
import { useUpdateQuestion } from "@/features/question/hooks/useMutation/useUpdateQuestion";
import { DEFAULT_CRM_SYSTEM } from "@/features/crm_system/lib/_core/crmSystem/crmSystem.constants.gen";
import { QuestionWithDependents, useGetQuestionsBySurveyId } from "@/features/question/hooks/useQuery/useGetQuestionsBySurveyId";
import { CheckProjectAccess } from "@/features/rbac/components/molecules/CheckProjectAccess";

export const QuestionsListSection = () => {
    const { orgId, projectId, surveyId } = useParams();
    const questionId = useSearchParams()[0].get("questionId") || null;
    // const { organization } = useOrganizationStore();
    const { project } = useProjectStore();
    const { survey } = useSurveyStore();

    const [showArchivedQuestions, setShowArchivedQuestions] = useState(false);
    const [sortByCreatedAtAscending, setSortByCreatedAtAscending] = useState(true);

    const { data: questions, isLoading: isLoadingQuestions, error: questionsError } = useGetQuestionsBySurveyId({
        surveyId: survey?.id,
        isArchived: showArchivedQuestions,
        sortByCreatedAtAscending: false,
    });
    const {
        Icon: QuestionsSortByIcon,
        label: questionsSortByLabel,
        nextOption: questionsSortByNextOption,
        sortedResult: sortedQuestions,
    } = useSortByWithConfig(
        questionsSortConfig,
        questions?.questions ?? [],
        'created_at_desc'
    );
    const {
        nextOption: groupedQuestionsSortByNextOption,
        sortedResult: sortedGroupedQuestions,
    } = useSortByWithConfig(
        questionsSortConfig,
        questions?.grouped ?? [],
        'created_at_desc'
    );
    if (questionsError) console?.error("Error loading questions:", questionsError);


    // * Handle Edit Question Dialog
    const [showEditQuestion, setShowEditQuestion] = useState(questionId !== null);
    const [selectedQuestionId, setSelectedQuestionId] = useState<string | null>(questionId);
    // Database['public']['Tables']['question']['Row'];

    const editSurveyHandler = (surveyId: string) => {
        setSelectedQuestionId(surveyId);
        setShowEditQuestion(true);
    }
    const hideEditQuestionHandler = () => {
        setShowEditQuestion(false);
        setSelectedQuestionId(null);
    }

    // * Handle Archive, Restore and Delete Question
    const { mutateAsync: archiveQuestion, isPending: isPendingArchiveQuestion } = useArchiveQuestion();
    const { mutateAsync: restoreQuestion, isPending: isPendingRestoreQuestion } = useRestoreQuestion();
    const { mutateAsync: deleteQuestion, isPending: isPendingDeleteQuestion } = useDeleteQuestion();

    const archiveQuestionHandler = async (survey_id: string) => {
        const userDecision = await DecisionModal({ title: "Archive this question?", confirmButtonText: "Archive", message: 'You will be able to restore this question later' });
        if (!userDecision.isConfirmed) return;

        try {
            await archiveQuestion(survey_id);
            SuccessPopup("Question archived successfully");
        } catch (error) {
            ErrorPopup("Error archiving question");
        }
    };

    const restoreQuestionHandler = async (survey_id: string) => {
        const userDecision = await DecisionModal({ title: "Restore", confirmButtonText: "Restore", message: 'Do you really want to restore this question?' });
        if (!userDecision.isConfirmed) return;

        try {
            await restoreQuestion(survey_id);
            SuccessPopup("Question restored successfully");
        } catch (error) {
            ErrorPopup("Error restoring question");
        }
    };

    const deleteQuestionHandler = async (survey_id: string) => {
        const userDecision = await DecisionModal({ title: "Delete", confirmButtonText: "Delete", message: 'Do you really want to delete this question? Once deleted, you will not be able to recover this question' });
        if (!userDecision.isConfirmed) return;

        try {
            await deleteQuestion(survey_id);
            SuccessPopup("Question deleted successfully");
        } catch (error) {
            ErrorPopup("Error deleting question");
        }
    };

    /*
    * Handle Add Question Dialog
    */
    const [showAddModal, setShowAddModal] = useState(false);

    const { mutateAsync: addQuestion, isPending: isPendingAddQuestion } = useAddQuestion();

    const addQuestionModalSubmitHandler = async (question: QuestionFormSchemaType) => {
        if (!surveyId) throw new Error("Missing Survey ID");
        if (!orgId) throw new Error("Missing Organization ID")

        try {
            await addQuestion({
                ...question,
                survey_id: surveyId,
                crm_system: question.crm_system || DEFAULT_CRM_SYSTEM,
                organisation_id: Number(orgId),
                field_type_context_stack: [],
                // selected_configuration: {
                //     parameter: question.selected_configuration?.parameter,
                //     conditionals: question.selected_configuration?.conditionals || {
                //         enabled: false,
                //         where: {
                //             or: [{ and: [] }]
                //         },
                //     }
                // }
            });
            SuccessPopup("Question added successfully");
        } catch (error) {
            if (error instanceof Error) {
                ErrorPopup(error.message);
            }
            ErrorPopup("Error adding question");
        }
    }
    const [sortQuestionsByDependencies, setSortQuestionsByDependencies] = useState(true);
    const { mutateAsync: editQuestion } = useUpdateQuestion()
    const onEditQuestionSubmitHandler = async (values: QuestionFormSchemaType) => {
        // console.log(values)
        // if (!surveyId || !orgId || !project?.crm_id) throw new Error("Missing Survey ID, Organisation ID or ProjectID.");
        await editQuestion({
            data: values
        }).then(() => {
            SuccessPopup("Question updated successfully");
            hideEditQuestionHandler();
        }).catch((error) => {

            if (error instanceof Error) {
                ErrorPopup(error.message || "Error updating question");
            }
            else {
                ErrorPopup("Error updating question");
            }
        })
    }
    return (
        <div className="flex flex-col flex-1">
            {(isPendingArchiveQuestion || isPendingRestoreQuestion || isPendingDeleteQuestion || isPendingAddQuestion) && (<LoaderWithBackdrop />)}

            <AddQuestionModal
                show={showAddModal}
                onHide={() => setShowAddModal(false)}
                onSubmit={addQuestionModalSubmitHandler}
                crm={project?.crm_project?.crm}
                crmProject={project?.crm_project}
                crmConfig={survey?.crm_config}
                questions={questions?.questions}
                crmSystem={project?.crm_system}
            />

            <EditQuestionModal
                show={showEditQuestion}
                onHide={hideEditQuestionHandler}
                questionData={questions?.questions?.find(q => q.id === selectedQuestionId)}
                isQuestionPropertiesDisabled={questions?.questions?.find(q => q.id === selectedQuestionId)?.has_response || false}
                onSubmit={onEditQuestionSubmitHandler}
                crm={project?.crm_project?.crm}
                crmProject={project?.crm_project}
                crmConfig={survey?.crm_config}
            />

            <div className="flex flex-col flex-1 gap-2">
                <SectionTitle
                    title="Questions List"
                >
                    <TooltipProvider>
                        <div className="flex justify-center gap-1">
                            {/* <SortOrderButton
                                disabled={isLoadingQuestions}
                                onClick={() => setSortByCreatedAtAscending(prev => !prev)}
                                sortByAscending={sortByCreatedAtAscending}
                                ascendingLabel="Sort By Ascending Date"
                                descendingLabel="Sort By Descending Date"
                            /> */}
                            <TooltipButton
                                tooltipContent={`Sort by ${questionsSortByLabel}`}
                                size='action'
                                variant='secondary'
                                onClick={()=>{
                                    questionsSortByNextOption();
                                    groupedQuestionsSortByNextOption();
                                }}
                            >
                                {QuestionsSortByIcon}
                            </TooltipButton>
                            <SortArchiveButton
                                disabled={isLoadingQuestions}
                                onClick={() => setShowArchivedQuestions(prev => !prev)}
                                showArchived={showArchivedQuestions}
                                activeLabel="Show Active Questions"
                                archivedLabel="Show Archived Questions"
                            />
                            <TooltipButton
                                tooltipContent="Group questions by dependencies"
                                size='action'
                                variant='secondary'
                                onClick={() => { setSortQuestionsByDependencies(prev => !prev) }}
                                disabled={isLoadingQuestions}
                            >
                                {sortQuestionsByDependencies ? <ListTree size={16} /> : <List size={16} />}
                            </TooltipButton>
                            <CheckProjectAccess question_create>
                                <AddQuestionIconButton
                                    disabled={isLoadingQuestions}
                                    onClick={() => setShowAddModal(true)}
                                />
                            </CheckProjectAccess>
                        </div>
                    </TooltipProvider>
                </SectionTitle>

                {(() => {
                    if (isLoadingQuestions) return (
                        <div className="grid h-full place-items-center">
                            <Loader className="animate-spin" />
                        </div>
                    )
                    if (questionsError) return (
                        <div className="grid h-full place-items-center">
                            <p>Error loading questions</p>
                        </div>
                    )
                    return (questions?.questions?.length === 0)
                        ? (
                            <div className="grid h-full place-items-center">
                                <p>No questions found</p>
                            </div>
                        ) : (
                            <ScrollArea className="pe-4" type="always"
                                // style={{ height: scrollAreaHeight - titleRowHeightWithGap }} 
                                style={{ height: "calc(100vh - 64px - 8px - 28px - 8px - 36px - 8px - 1px - 8px - 28px - 8px - 8px - 28px - 8px)", minHeight: 200 }}
                            >
                                <div className="flex flex-col gap-2">

                                    {(() => {
                                        if (sortQuestionsByDependencies) {
                                            return sortedGroupedQuestions?.map((question) => (
                                                <QuestionsGroupedListRow
                                                    key={question.id}
                                                    question={question}
                                                    showArchiveButton={!showArchivedQuestions}
                                                    onEdit={editSurveyHandler}
                                                    onArchive={archiveQuestionHandler}
                                                    onRestore={restoreQuestionHandler}
                                                    onDelete={deleteQuestionHandler}
                                                    orgId={orgId}
                                                    projectId={projectId}
                                                    surveyId={surveyId}
                                                />
                                            ))
                                        } else {
                                            return sortedQuestions?.map((question, index) => (
                                                <ListRow
                                                    key={question.id}
                                                    index={index + 1}
                                                    link={`/organization/${orgId}/project/${projectId}/survey/${surveyId}/manage-survey/questions?questionId=${question.id}`}
                                                    title={question.question || "-"}
                                                    description={question.description || "-"}
                                                    createdAt={question.created_at ? new Date(question.created_at).toLocaleDateString('en-US', {
                                                        year: 'numeric',
                                                        month: 'long',
                                                        day: 'numeric'
                                                    }) : undefined}
                                                    showArchiveButton={!showArchivedQuestions}
                                                    onEdit={() => editSurveyHandler(question.id)}
                                                    onArchive={() => archiveQuestionHandler(question.id)}
                                                    onRestore={() => restoreQuestionHandler(question.id)}
                                                    onDelete={() => deleteQuestionHandler(question.id)}
                                                />
                                            ))
                                        }
                                    })()}
                                </div>
                            </ScrollArea>
                        )
                })()}
            </div>

        </div>
    )
}

function QuestionsGroupedListRow({
    question,
    showArchiveButton,
    onEdit,
    onArchive,
    onRestore,
    onDelete,
    orgId,
    projectId,
    surveyId,
}: {
    question: QuestionWithDependents,
    showArchiveButton?: boolean,
    onEdit: (questionId: string) => void,
    onArchive: (questionId: string) => void,
    onRestore: (questionId: string) => void,
    onDelete: (questionId: string) => void,
    orgId: string | undefined,
    projectId: string | undefined,
    surveyId: string | undefined,
}) {
    return (
        <div className="flex flex-col gap-2">
            <div className={`flex items-center justify-between p-2 bg-gray-100 rounded-md gap-3 hover:bg-gray-200 transition-all duration-200 ease-in-out`}>
                <Link to={`/organization/${orgId}/project/${projectId}/survey/${surveyId}/manage-survey/questions?questionId=${question.id}`}
                    // className="flex items-center w-full gap-2"
                    className={cn({
                        "flex items-center w-full gap-2": true,
                    })}
                >
                    <div className="w-full cursor-pointer">
                        <p className="w-full pr-4 text-xs font-medium line-clamp-1">{question.question}</p>
                        <p className="w-full pr-4 text-xs line-clamp-2">{question.description || '-'}</p>
                        {question.created_at && (<p className="w-full pr-4 text-[10px] line-clamp-1">{formatDate(question.created_at)}</p>)}
                    </div>
                </Link>

                <CheckProjectAccess question_update>
                    <div className="flex items-center justify-center gap-1">
                        <TooltipButton tooltipContent="Edit" size='action' onClick={() => onEdit(question.id)}>
                            <Pencil size={16} />
                        </TooltipButton>
                        {showArchiveButton && (
                            <TooltipButton tooltipContent="Archive" variant='accent' size='action' onClick={() => onArchive(question.id)}>
                                <Archive size={16} />
                            </TooltipButton>
                        )}
                        {!showArchiveButton && (
                            <TooltipButton tooltipContent="Restore" variant='accent' size='action' onClick={() => onRestore(question.id)}>
                                <ArchiveRestore size={16} />
                            </TooltipButton>
                        )}
                        {!showArchiveButton && (
                            <TooltipButton tooltipContent="Delete" variant='destructive' size='action' onClick={() => onDelete(question.id)}>
                                <Trash2 size={16} />
                            </TooltipButton>
                        )}
                    </div>
                </CheckProjectAccess>
            </div>
            {question.dependent_questions.length > 0 && (
                <div className="flex flex-col gap-2 pl-2 ml-2 conditional-group-border">
                    {question.dependent_questions.map((dependentQuestion) => (
                        <QuestionsGroupedListRow
                            key={dependentQuestion.id}
                            question={dependentQuestion}
                            showArchiveButton={showArchiveButton}
                            onEdit={onEdit}
                            onArchive={onArchive}
                            onRestore={onRestore}
                            onDelete={onDelete}
                            orgId={orgId}
                            projectId={projectId}
                            surveyId={surveyId}
                        />
                    ))}
                </div>
            )}
        </div>
    )
}

function formatDate(date: string | Date): string {
    if (typeof date === 'string') {
        date = new Date(date);
    }
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });
}